/* eslint-disable react-hooks/exhaustive-deps -- effects intentionally run on mount / on the listed deps; adding the flagged functions risks render loops. */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  Add as AddIcon,
  Style as StyleIcon,
  Event as EventIcon,
  Flight as FlightIcon,
  Collections as CollectionsIcon,
  TrendingUp as TrendingIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { activityAPI } from '../services/api';
import { Activity } from '../types';
import { formatDistanceToNow } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`activity-tabpanel-${index}`}
      aria-labelledby={`activity-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface ActivityFeedProps {
  currentUserId: string;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ currentUserId }) => {
  const [tabValue, setTabValue] = useState(0);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [userActivities, setUserActivities] = useState<Activity[]>([]);
  const [trendingActivities, setTrendingActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadActivities();
  }, [currentUserId, tabValue]);

  const loadActivities = async (pageNum: number = 1) => {
    try {
      setLoading(pageNum === 1);
      setError(null);

      let response: any;
      const offset = (pageNum - 1) * 20;
      const params = { offset, limit: 20 };

      switch (tabValue) {
        case 0:
          response = await activityAPI.getActivityFeed(params);
          if (pageNum === 1) {
            setActivities(response.data.activities);
          } else {
            setActivities(prev => [...prev, ...response.data.activities]);
          }
          break;
        case 1:
          response = await activityAPI.getUserActivities(currentUserId, params);
          if (pageNum === 1) {
            setUserActivities(response.data.activities);
          } else {
            setUserActivities(prev => [...prev, ...response.data.activities]);
          }
          break;
        case 2:
          response = await activityAPI.getTrendingActivities(params);
          if (pageNum === 1) {
            setTrendingActivities(response.data.activities);
          } else {
            setTrendingActivities(prev => [...prev, ...response.data.activities]);
          }
          break;
        default:
          return;
      }

      setHasMore(response.data.activities.length === 20);
      setPage(pageNum);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreActivities = () => {
    if (!loading && hasMore) {
      loadActivities(page + 1);
    }
  };

  const getActivityIcon = (activityType: string) => {
    const iconMap: Record<string, React.ReactElement> = {
      item_added: <AddIcon />,
      item_worn: <StyleIcon />,
      outfit_created: <CollectionsIcon />,
      outfit_worn: <StyleIcon />,
      trip_created: <FlightIcon />,
      event_created: <EventIcon />,
      friend_added: <PersonIcon />,
      item_favorited: <FavoriteIcon />,
      outfit_favorited: <FavoriteIcon />
    };

    return iconMap[activityType] || <StyleIcon />;
  };

  const getActivityTypeColor = (activityType: string) => {
    const colorMap: Record<string, any> = {
      item_added: 'primary',
      item_worn: 'success',
      outfit_created: 'secondary',
      outfit_worn: 'success',
      trip_created: 'info',
      event_created: 'warning',
      friend_added: 'primary',
      item_favorited: 'error',
      outfit_favorited: 'error'
    };

    return colorMap[activityType] || 'default';
  };

  const formatActivityType = (activityType: string) => {
    return activityType.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const renderActivity = (activity: Activity) => {
    return (
      <ListItem key={activity.id} alignItems="flex-start">
        <ListItemAvatar>
          <Avatar src={activity.user?.avatar_url}>
            {activity.user?.display_name.charAt(0).toUpperCase()}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="subtitle1" component="span">
                {activity.user?.display_name}
              </Typography>
              <Chip
                icon={getActivityIcon(activity.activity_type)}
                label={formatActivityType(activity.activity_type)}
                size="small"
                color={getActivityTypeColor(activity.activity_type)}
                variant="outlined"
              />
              <Typography variant="caption" color="textSecondary">
                {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
              </Typography>
            </Box>
          }
          secondary={
            <Box>
              <Typography variant="body2" color="textPrimary" gutterBottom>
                {activity.title}
              </Typography>
              {activity.description && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {activity.description}
                </Typography>
              )}
              {activity.metadata && (
                <Box mt={1}>
                  {activity.metadata.item_name && (
                    <Chip label={activity.metadata.item_name} size="small" sx={{ mr: 1 }} />
                  )}
                  {activity.metadata.outfit_name && (
                    <Chip label={activity.metadata.outfit_name} size="small" sx={{ mr: 1 }} />
                  )}
                  {activity.metadata.category && (
                    <Chip label={activity.metadata.category} size="small" sx={{ mr: 1 }} />
                  )}
                  {activity.metadata.wear_count && (
                    <Chip 
                      label={`${activity.metadata.wear_count} wears`} 
                      size="small" 
                      color="success"
                      sx={{ mr: 1 }} 
                    />
                  )}
                </Box>
              )}
            </Box>
          }
        />
      </ListItem>
    );
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">Activity Feed</Typography>
        </Box>

        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => {
            setTabValue(newValue);
            setPage(1);
            setHasMore(true);
          }} 
          sx={{ mb: 3 }}
        >
          <Tab icon={<StyleIcon />} label="Feed" />
          <Tab icon={<PersonIcon />} label="My Activity" />
          <Tab icon={<TrendingIcon />} label="Trending" />
        </Tabs>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TabPanel value={tabValue} index={0}>
          {loading && activities.length === 0 ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {activities.length === 0 ? (
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      No activity yet
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Start adding items to your wardrobe and creating outfits to see activity here.
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      You'll also see activity from your friends once you connect with them!
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {activities.map((activity, index) => (
                    <React.Fragment key={activity.id}>
                      {renderActivity(activity)}
                      {index < activities.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                  
                  {hasMore && (
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Button 
                        onClick={loadMoreActivities}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={20} /> : 'Load More'}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </List>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {loading && userActivities.length === 0 ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {userActivities.length === 0 ? (
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      No personal activity yet
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Start using the app to track your activity!
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {userActivities.map((activity, index) => (
                    <React.Fragment key={activity.id}>
                      {renderActivity(activity)}
                      {index < userActivities.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                  
                  {hasMore && (
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Button 
                        onClick={loadMoreActivities}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={20} /> : 'Load More'}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </List>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {loading && trendingActivities.length === 0 ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {trendingActivities.length === 0 ? (
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      No trending activity
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Check back later for trending activities from the community!
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {trendingActivities.map((activity, index) => (
                    <React.Fragment key={activity.id}>
                      {renderActivity(activity)}
                      {index < trendingActivities.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                  
                  {hasMore && (
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Button 
                        onClick={loadMoreActivities}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={20} /> : 'Load More'}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </List>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default ActivityFeed;